import {take, select, fork, put, delay} from 'redux-saga/effects';
import {closeModal} from '@computerrock/formation-router/sagas';
import {replace, resolveRoute} from '@computerrock/formation-router';
import {LeistContact} from '@ace-de/eua-entity-types';
import modalIds from '../../modalIds';
import fetchRequest from '../../application/sagas/fetchRequest';
import contactDataModalTypes from '../modals/contactDataModalTypes';
import * as contactActionTypes from '../contactActionTypes';
import * as invoiceActionTypes from '../../invoices/invoiceActionTypes';
import bcModalContentTypes from '../modals/bcModalContentTypes';
import bcActionFailedModalTypes from '../modals/bcActionFailedModalTypes';
import config from '../../config';

const editContactModalFlow = function* editContactModalFlow() {
    let shouldWaitForAction = true;
    let payloadData = null;
    let shouldRetryEditContact = false;
    let retryEditContactData = null;

    const {serviceManager} = yield select(state => state.application);
    const leaAssignmentFlowService = serviceManager.loadService('leaAssignmentFlowService');

    while (true) {
        if (shouldWaitForAction) {
            const {payload} = yield take(contactActionTypes.INITIATE_EDIT_CONTACT_MODAL_FLOW);
            payloadData = payload;
        }
        const {location} = yield select(state => state.router);
        const isModalOpened = !!(new URLSearchParams(location.search).get('modal'));

        const {client, selectedContact, invoiceId: isOriginInvoicePage} = payloadData;
        let chosenModalOption;

        if (selectedContact) {
            yield put({
                type: contactActionTypes.STORE_CONTACTS,
                payload: {contactDTOs: [selectedContact]},
            });
        }

        if (!shouldRetryEditContact && client) {
            const searchParams = new URLSearchParams(location.search);
            if (!isModalOpened) {
                searchParams.set('modal', modalIds.BC_CONTACT_MODAL);
            }
            searchParams.set('contentType', bcModalContentTypes.CONTACT_DATA);
            searchParams.set('client', client);
            searchParams.set('type', contactDataModalTypes.EDIT_CONTACT);
            searchParams.set('contactId', selectedContact.id);

            yield put(replace(resolveRoute(location.pathname, {}, {search: searchParams.toString()})));

            chosenModalOption = yield take([
                contactActionTypes.RETURN_TO_CONTACT_SEARCH,
                contactActionTypes.CONFIRM_EDIT_CONTACT,
            ]);
        }

        if (shouldRetryEditContact || chosenModalOption.type === contactActionTypes.CONFIRM_EDIT_CONTACT) {
            const contactId = !shouldRetryEditContact && chosenModalOption?.payload?.contactId
                ? chosenModalOption?.payload?.contactId : retryEditContactData?.contactId;

            const contactData = !shouldRetryEditContact && chosenModalOption?.payload?.contactData
                ? chosenModalOption?.payload?.contactData : retryEditContactData?.contactData;

            const invoiceId = !shouldRetryEditContact && chosenModalOption?.payload?.invoiceId
                ? chosenModalOption?.payload?.invoiceId : retryEditContactData?.invoiceId;

            const client = !shouldRetryEditContact && chosenModalOption?.payload?.client
                ? chosenModalOption?.payload?.client : retryEditContactData?.client;

            const loaderSearchParams = new URLSearchParams(location.search);
            if (!isModalOpened) {
                loaderSearchParams.set('modal', modalIds.BC_CONTACT_MODAL);
            }
            loaderSearchParams.set('contentType', bcModalContentTypes.LOADER);
            loaderSearchParams.set('type', 'EDIT_CONTACT');

            yield put(replace(resolveRoute(location.pathname, {}, {search: loaderSearchParams.toString()})));

            yield fork(fetchRequest,
                contactActionTypes.UPDATE_CONTACT_REQUEST,
                leaAssignmentFlowService.updateContact,
                {
                    contactId,
                    contactData: {
                        client,
                        ...LeistContact.objectToDTO(contactData),
                    },
                });

            const responseAction = yield take([
                contactActionTypes.UPDATE_CONTACT_REQUEST_SUCCEEDED,
                contactActionTypes.UPDATE_CONTACT_REQUEST_FAILED,
            ]);

            yield delay(100);
            shouldRetryEditContact = false;

            if (!responseAction.error) {
                const {response} = responseAction.payload;
                const {contactDTO} = response;
                const contactDTOs = [contactDTO];

                yield put({
                    type: contactActionTypes.STORE_CONTACTS,
                    payload: {contactDTOs},
                });

                shouldWaitForAction = true;

                yield* closeModal(modalIds.BC_CONTACT_MODAL, {contentType: '', type: '', client: '', contactId: ''});
                yield delay(100);
                if (isOriginInvoicePage) {
                    yield put({
                        type: invoiceActionTypes.INITIATE_CONTACT_SELECTION_FLOW,
                        payload: {
                            invoiceId,
                            client,
                            contactId: contactDTO.id,
                            initiateContactSearch: true,
                        },
                    });
                }
                continue;
            }

            if (responseAction.error) {
                if (responseAction.payload?.response?.status === config.STATUS_CODE_SERVICE_UNAVAILABLE) {
                    yield put({
                        type: contactActionTypes.SET_BC_ERROR_MESSAGE,
                        payload: {errorDescription: JSON.parse(responseAction.payload.message)},
                    });
                }

                const failedQueryParams = new URLSearchParams(location.search);

                if (!isModalOpened) {
                    failedQueryParams.set('modal', modalIds.BC_CONTACT_MODAL);
                }
                failedQueryParams.set('contentType', bcModalContentTypes.BC_ACTION_FAILED);
                failedQueryParams.set('type', bcActionFailedModalTypes.EDIT_CONTACT);
                yield put(replace(resolveRoute(location.pathname, {}, {search: failedQueryParams.toString()})));

                const nextAction = yield take([
                    contactActionTypes.RETRY_EDIT_CONTACT,
                    contactActionTypes.CANCEL_ACTION,
                ]);

                if (nextAction.type === contactActionTypes.RETRY_EDIT_CONTACT) {
                    shouldRetryEditContact = true;
                    shouldWaitForAction = false;
                    retryEditContactData = {
                        contactData,
                        contactId,
                        invoiceId,
                    };
                    continue;
                }

                if (nextAction.type === contactActionTypes.CANCEL_ACTION) {
                    shouldWaitForAction = false;
                    shouldRetryEditContact = false;
                    continue;
                }
            }
        }

        shouldWaitForAction = true;
        if (isOriginInvoicePage) {
            const searchParams = new URLSearchParams(location.search);
            searchParams.set('contentType', bcModalContentTypes.INVOICE_CONTACT_SEARCH);
            searchParams.set('client', client);

            yield put(replace(resolveRoute(location.pathname, {}, {search: searchParams.toString()})));
            continue;
        }

        yield* closeModal(modalIds.BC_CONTACT_MODAL, {contentType: '', type: '', contactId: ''});
    }
};

export default editContactModalFlow;
